/* eslint-disable @typescript-eslint/no-namespace */
import { make, type Brand } from "ts-brand";
import { type ServiceDefinition, type ServiceProvider } from "../configuration";
import { type DocumentTemplateText } from "./DocumentTemplateText";
import { Template } from "./Template";

const templateNames = ["quote", "order-confirmation"] as const;

export type DocumentTemplateData = {
    /**
     * The name of the document template.
     */
    name: DocumentTemplate.Name;
    /**
     * The HTML content of the document template.
     */
    html: string;
    /**
     * The customizable text content for the document template (labels, messages, etc.).
     */
    text?: DocumentTemplateText;
};

export interface DocumentTemplate extends DocumentTemplateData, Template {
    /**
     * The id of the document template.
     */
    id: DocumentTemplate.Id;
}

export namespace DocumentTemplate {
    /**
     * The id of the document template to identify it.
     *
     * Must be a non-empty ID string.
     */
    export type Id = Brand<string, DocumentTemplate>;

    /**
     * The name of the document template.
     */
    export type Name = (typeof templateNames)[number];

    /**
     * Internal ID factory using ts-brand
     */
    export const Id = make<Id>(value => {
        if (!value || value.trim() === "") {
            throw new Error("DocumentTemplate: Property 'id' cannot be empty.");
        }
    });

    /**
     * Creates a new document template ID.
     *
     * @param templateName The template name.
     * @param providerId Optional provider ID for provider-specific templates.
     * @param serviceId Optional service ID for service-specific templates.
     * @returns The new document template ID.
     */
    export const createId = (
        templateName: DocumentTemplate.Name,
        providerId?: ServiceProvider.Id,
        serviceId?: ServiceDefinition.Id,
        shortId?: string
    ): Id => {
        return Template.createId<Id>(templateName, templateNames, providerId, serviceId, shortId);
    };

    export const Name = (value: string): Name => {
        return Template.Name<Name>(value, templateNames);
    };

    /**
     * Creates a new document template ID.
     *
     * @param id The document template ID.
     * @returns An object with templateName, providerId, and serviceId.
     * @throws An error if the ID is empty.
     */
    export const parse = (
        id: Id
    ): {
        templateName: DocumentTemplate.Name;
        providerId?: ServiceProvider.Id;
        serviceId?: ServiceDefinition.Id;
    } => {
        return Template.parse<Name>(id);
    };

    /**
     * Gets the template name from a document template ID.
     *
     * @param id The document template ID.
     * @returns The template name.
     */
    export const getName = (id: Id): DocumentTemplate.Name => {
        return Template.parse<Name>(id).templateName;
    };
}
